/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { AccordionSummary, AppBar, Box, Divider, Drawer, List, ListItem, ListItemButton, ListItemText, Stack, Typography } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import NavigationBarLogo from "@/src/components/Navigation/NavigationBar/NavigationBarLogo";
import BurgerButton from "@/src/components/Navigation/BurgerButton/BurgerButton";
import SubnavigationTabs from "@/src/components/Navigation/NavigationBar/SubnavigationTabs";
import IconButtonLegalbird from "@/src/components/legalbird/IconButtonLegalbird/IconButtonLegalbird";
import CloseIcon from "@mui/icons-material/Close";
import Accordion from "@mui/material/Accordion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionDetails from "@mui/material/AccordionDetails";
import { Link, useLocation } from "react-router";
import HeaderActions from "@/src/components/Navigation/HeaderActions/HeaderActions";
import React, { useId, useState } from "react";
import { NavigationBarDesktopProps } from "@/src/components/Navigation/NavigationBar/Desktop/NavigationBarDesktop";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";

type NavigationBarMobileNonAuthenticatedProps = {
  hideActions?: boolean;
  clickableLogo?: boolean;
  mainNavigationItems: NavigationBarDesktopProps["mainNavigationItems"];
};

export default function NavigationBarMobileNonAuthenticated({ hideActions, clickableLogo, mainNavigationItems }: NavigationBarMobileNonAuthenticatedProps) {
  const accordionId = useId();
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const mainNavigationItemCategory = mainNavigationItems[0];

  const secondLevelNavValue =
    mainNavigationItemCategory?.type === "category" && mainNavigationItemCategory.categoryMapping.find((tab) => pathname.includes(tab.to))?.to;

  return (
    <>
      <AppBar sx={(theme) => ({ backgroundColor: theme.palette.background.default })} elevation={0}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <NavigationBarLogo clickableLogo={clickableLogo} />
          {(!mainNavigationItems.length && hideActions) || <BurgerButton onClick={() => setMobileNavOpen(true)} />}
        </Toolbar>
        {!!secondLevelNavValue && (
          <Box sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.divider}` })}>
            <SubnavigationTabs
              value={secondLevelNavValue}
              items={mainNavigationItemCategory.type === "category" ? mainNavigationItemCategory.categoryMapping : []}
            />
          </Box>
        )}
      </AppBar>
      <Drawer
        anchor={"top"}
        open={mobileNavOpen}
        slotProps={{
          paper: {
            sx: {
              height: "100%",
            },
          },
        }}
      >
        <Stack sx={{ height: "100%" }}>
          <Stack sx={{ flexDirection: "row", justifyContent: "space-between", padding: "1rem" }}>
            <NavigationBarLogo clickableLogo={clickableLogo} />
            <IconButtonLegalbird label={"Schließen"} variant={"text"} color={"primary"} onClick={() => setMobileNavOpen(false)}>
              <CloseIcon />
            </IconButtonLegalbird>
          </Stack>
          <Divider />
          <List disablePadding sx={{ paddingX: "1rem", paddingTop: "1.5rem" }}>
            {mainNavigationItems.map((item) => {
              if (item.type === "category") {
                return (
                  <ListItem divider={true} disableGutters disablePadding>
                    <Accordion disableGutters elevation={0} sx={{ width: "100%" }}>
                      <AccordionSummary
                        sx={{ padding: 0 }}
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${accordionId}-panel1-content`}
                        id={`${accordionId}-panel1-header`}
                      >
                        <Typography component="span" variant={"h5"} sx={{ margin: 0 }}>
                          {mainNavigationItemCategory.label}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ padding: 0, marginBottom: "1rem" }}>
                        <List>
                          {mainNavigationItemCategory.type === "category" &&
                            mainNavigationItemCategory.categoryMapping.map((item) => (
                              <ListItem disableGutters>
                                <ListItemButton component={Link} to={item.to} disableGutters sx={{ padding: 0 }}>
                                  <ListItemText primary={item.label} sx={{ margin: 0 }} />
                                </ListItemButton>
                              </ListItem>
                            ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  </ListItem>
                );
              }

              return (
                <ListItem divider={true} disableGutters disablePadding>
                  <ListItemButton component={Link} to={item.to} disableGutters>
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: {
                          variant: "h5",
                          sx: {
                            margin: 0,
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          <Box sx={{ flexGrow: 1 }} />
          {!hideActions && (
            <Box sx={{ paddingX: "1rem", paddingBottom: "2rem" }}>
              <HeaderActions />
            </Box>
          )}
        </Stack>
      </Drawer>
    </>
  );
}
